import React, { CSSProperties, ReactElement } from "react";
import { wire } from "react-hot-wire";

import { WithTranslation, withTranslation } from "react-i18next";

import Chip from "@material-ui/core/Chip";
import Avatar from "@material-ui/core/Avatar";

import IconCheckCircle from "@material-ui/icons/CheckCircle";
import IconLoop from "@material-ui/icons/Loop";
import IconError from "@material-ui/icons/Error";
import IconOfflineBolt from "@material-ui/icons/OfflineBolt";
import IconArchive from "@material-ui/icons/Archive";
import IconErase from "@material-ui/icons/DeleteForever";
import IconExitToApp from "@material-ui/icons/ExitToApp";
import IconEdit from "@material-ui/icons/Edit";
import IconImportExport from "@material-ui/icons/ImportExport";
import IconPersonAdd from "@material-ui/icons/PersonAdd";

import { SiteService } from "services/site/Site.service";
import { DrawerService } from "services/drawer/Drawer.service";

import Menu from "components/common/menu/Menu";

import { Table } from "components/common/table/Table";
import { Query, TableColumn } from "components/common/table/types";
import { Application } from "models/application/Application";

const status: {
  [status: string]: {
    label: string;
    icon: ReactElement;
    chipStyle: CSSProperties;
    avatarStyle: CSSProperties;
  };
} = {
  failed: {
    label: "Failed",
    icon: <IconError style={{ color: "#FFF" }} />,
    chipStyle: { backgroundColor: "#de4b4b", color: "#FFF" },
    avatarStyle: { backgroundColor: "#903131" }
  },
  successful: {
    label: "Online",
    icon: <IconCheckCircle style={{ color: "#FFF" }} />,
    chipStyle: { backgroundColor: "#4fba2c", color: "#FFF" },
    avatarStyle: { backgroundColor: "#39871f" }
  },
  "in-progress": {
    label: "Loading",
    icon: <IconLoop className="spin" style={{ color: "#FFF" }} />,
    chipStyle: { backgroundColor: "#e0dd25", color: "#FFF" },
    avatarStyle: { backgroundColor: "#adab1c" }
  },
  pending: {
    label: "Pending",
    icon: <IconLoop className="spin" style={{ color: "#FFF" }} />,
    chipStyle: { backgroundColor: "#e0dd25", color: "#FFF" },
    avatarStyle: { backgroundColor: "#adab1c" }
  },
  offline: {
    label: "Offline",
    icon: <IconOfflineBolt style={{ color: "#FFF" }} />,
    chipStyle: { backgroundColor: "#de4b4b", color: "#FFF" },
    avatarStyle: { backgroundColor: "#903131" }
  },
  archived: {
    label: "Archived",
    icon: <IconArchive style={{ color: "#FFF" }} />,
    chipStyle: { backgroundColor: "#989898", color: "#FFF" },
    avatarStyle: { backgroundColor: "#616060" }
  },
  erased: {
    label: "Erased",
    icon: <IconErase style={{ color: "#FFF" }} />,
    chipStyle: { backgroundColor: "#000000de", color: "#FFF" },
    avatarStyle: { backgroundColor: "#616060" }
  }
};

function ArchivedApplicationList({
  siteService,
  drawerService,
  t
}: {
  siteService: SiteService;
  drawerService: DrawerService;
} & WithTranslation) {
  return (
    <div className="m-3">
      <Table<Application>
        title={t("application.list.archived.title")}
        dataFetcher={(query: Query) =>
          siteService.applicationList({ query, archived: true })
        }
        options={{
          search: true,
          pagination: true,
          refresh: 5000,
          sortingMode: "single"
        }}
        addAction={actions =>
          drawerService.activate("create-application", {
            action: actions.refresh
          })
        }
        columns={
          [
            {
              field: "id",
              title: t("application.list.column.id"),
              order: "desc"
            },
            {
              field: "createdAt",
              title: t("application.list.column.createdAt"),
              cellClassName: "text-nowrap"
            },
            {
              field: "name",
              title: t("application.list.column.name"),
              cellClassName: "text-nowrap",
              render: (application, actions) => (
                <a
                  href="/"
                  onClick={event => {
                    event.preventDefault();

                    drawerService.activate("edit-application", {
                      application: application,
                      action: actions.refresh
                    });
                  }}
                >
                  {application.name}
                </a>
              )
            },
            {
              field: "mainDomainName",
              title: t("application.list.column.domain"),
              sorting: false,
              cellClassName: "text-nowrap",
              render: application =>
                application && (
                  <a
                    href={`https://${application.mainDomainName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {application.mainDomainName}
                    <IconExitToApp
                      color="disabled"
                      className="ml-1"
                      fontSize="small"
                    />
                  </a>
                )
            },
            {
              field: "accountManagerNote",
              title: t("application.list.column.accountManagerNote"),
              cellClassName: "text-nowrap"
            },
            {
              field: "note",
              title: t("application.list.column.note"),
              headerClassName: "w-100"
            },
            {
              field: "status",
              title: t("application.list.column.status"),
              sorting: false,
              render: application => (
                <Chip
                  label={t(`application.list.status.${application.status}`)}
                  clickable={true}
                  style={status[application.status].chipStyle}
                  onClick={() =>
                    drawerService.activate("preview-application", {
                      application
                    })
                  }
                  avatar={
                    <Avatar style={status[application.status].avatarStyle}>
                      {status[application.status].icon}
                    </Avatar>
                  }
                />
              )
            },
            {
              field: "action",
              title: t("application.list.column.actions"),
              sorting: false,
              render: (application, actions) =>
                application && (
                  <Menu
                    translationPath="application.list.rowMenu"
                    items={[
                      {
                        icon: <IconEdit fontSize="small" className="mr-2" />,
                        text: "edit",
                        onClick: () =>
                          drawerService.activate("edit-application", {
                            application,
                            action: actions.refresh
                          })
                      },
                      {
                        icon: (
                          <IconImportExport fontSize="small" className="mr-2" />
                        ),
                        text: "import",
                        onClick: () =>
                          drawerService.activate("import-application", {
                            application,
                            action: actions.refresh
                          })
                      },
                      {
                        icon: (
                          <IconPersonAdd fontSize="small" className="mr-2" />
                        ),
                        text: "addUser",
                        onClick: () =>
                          drawerService.activate("application-add-user", {
                            application,
                            action: actions.refresh
                          })
                      },
                      {
                        icon: <IconArchive fontSize="small" className="mr-2" />,
                        text: "archive",
                        onClick: () =>
                          siteService.archiveApplication({ application })
                      },
                      {
                        icon: <IconErase fontSize="small" className="mr-2" />,
                        text: "erase",
                        onClick: () =>
                          drawerService.activate("erase-application", {
                            application,
                            action: actions.refresh
                          })
                      }
                    ]}
                  />
                )
            }
          ] as TableColumn<Application>[]
        }
      />
    </div>
  );
}

export default wire(
  ["siteService", "drawerService"],
  withTranslation()(ArchivedApplicationList)
);
